import { addDays, addMonths, monthOf, weekStartOf } from '@wiztime/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { dayPath, monthPath, weekPath } from '../app/routes';
import { useMonthUi } from '../features/month/monthUiStore';
import { useRouteView, useToday, ViewKind } from '../hooks/useRouteView';
import { formatDateLabel, formatMonthLabel } from '../lib/format';
import { ChevronLeftIcon, ChevronRightIcon } from '../ui/icons';
import styles from './TopBar.module.css';

const DAYS_PER_WEEK = 7;

/**
 * ‹ date › and "Today"; `trailing` is an extra control at the end of the row (on a phone the top
 * bar's first row is full, so the action-history button sits here); steps by a day in the day view, a week in the week view, a month in the month view. */
export function DateNav({ trailing = null }: { trailing?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const view = useRouteView();
  const today = useToday();
  const kind = view.kind;
  if (view.date === null || (kind !== ViewKind.Day && kind !== ViewKind.Week && kind !== ViewKind.Month)) {
    return null;
  }

  const current = view.date;
  const year = Number(today.slice(0, 4));
  let label: string;
  let prevLabel: string;
  let nextLabel: string;
  let goPrev: () => void;
  let goNext: () => void;
  let goToday: () => void;
  let atToday: boolean;

  if (kind === ViewKind.Month) {
    const month = monthOf(current);
    label = formatMonthLabel(month, i18n.language);
    prevLabel = t('nav.prevMonth');
    nextLabel = t('nav.nextMonth');
    goPrev = () => void navigate(monthPath(addMonths(month, -1)));
    goNext = () => void navigate(monthPath(addMonths(month, 1)));
    // "Today" also moves the grid's focus to today (the month may already be on screen).
    goToday = () => {
      useMonthUi.getState().requestFocus(today, false);
      void navigate(monthPath(monthOf(today)));
    };
    atToday = false;
  } else {
    const isWeek = kind === ViewKind.Week;
    const step = isWeek ? DAYS_PER_WEEK : 1;
    const pathFor = isWeek ? weekPath : dayPath;
    const target = isWeek ? weekStartOf(today) : today;
    label = isWeek
      ? `${formatDateLabel(current, i18n.language, year)} – ${formatDateLabel(addDays(current, DAYS_PER_WEEK - 1), i18n.language, year)}`
      : formatDateLabel(current, i18n.language, year);
    prevLabel = isWeek ? t('nav.prevWeek') : t('nav.prevDay');
    nextLabel = isWeek ? t('nav.nextWeek') : t('nav.nextDay');
    goPrev = () => void navigate(pathFor(addDays(current, -step)));
    goNext = () => void navigate(pathFor(addDays(current, step)));
    goToday = () => void navigate(pathFor(target));
    atToday = current === target;
  }

  return (
    <div className={styles.dateNav} role="group" aria-label={t('nav.dateNav')}>
      <button type="button" className={styles.iconButton} aria-label={prevLabel} onClick={goPrev}>
        <ChevronLeftIcon />
      </button>
      <span className={styles.dateLabel} data-testid="date-label">
        {label}
      </span>
      <button type="button" className={styles.iconButton} aria-label={nextLabel} onClick={goNext}>
        <ChevronRightIcon />
      </button>
      <button type="button" className={styles.todayButton} disabled={atToday} onClick={goToday}>
        {kind === ViewKind.Week ? t('nav.thisWeek') : t('nav.today')}
      </button>
      {trailing}
    </div>
  );
}
